import { media } from '../../../webrtc/media';
import type { Publisher, PublisherBrokenReason } from './types';

export const getPublisherBrokenReason = ():
  | PublisherBrokenReason
  | undefined => {
  if (typeof window === 'undefined' || !window.OT) {
    return;
  }
  /**
   * Find all publishers that are not related to screen-sharing.
   * Publisher still might be missing the video source for example if media access got revoked.
   */
  const publishers = (
    window as unknown as { OT: { publishers: Publisher[] } }
  ).OT.publishers
    .map((p: Publisher) => p)
    .filter((p: Publisher) => p.getVideoSource().type !== 'screen');

  /**
   * If there are no publisher during the call - something is wrong.
   * It might be:
   *  - user started a call with cam/mic disabled.
   *  - publisher got destroyed but never recreated.
   */
  if (publishers.length === 0) {
    return 'psStatusPubPublisherMissing';
  }

  /**
   * Now we need to find a publishers with the video source with type `camera`
   * and make sure we do not have more then one.
   */
  const publishersWithCameraSource = publishers.filter(
    (p: Publisher) => p.getVideoSource().type === 'camera',
  );

  /**
   * If there are no publishers with camera source - it is a problem. Video will be disabled.
   * Maybe media access got revoked.
   */
  if (publishersWithCameraSource.length === 0) {
    return 'psStatusPubCameraSourceMissing';
  }
  /**
   * It must be only one publisher.
   * If we have 2 with video sources - it is a problem and might bring some inconsistent behaviour when
   * one publisher is published to the session but another one is used locally.
   */
  if (publishersWithCameraSource.length > 1) {
    return 'psStatusPubMultiplePublishers';
  }

  const publisher = publishersWithCameraSource[0] as Publisher;

  /**
   * If publisher has published successfully - it should have a stream.
   */
  if (!publisher.stream) {
    return 'psStatusPubStreamMissing';
  }

  /**
   * It could be a situation when the publisher that has published to the session is not
   * the same as we use inside the media. So basically you might publish your media and other might see you
   * but for you locally media is missing.
   */
  const videoTrack = publisher.getVideoSource().track;
  if (
    videoTrack &&
    videoTrack !== media.tracks.find((t) => t.kind === 'video')
  ) {
    return 'psStatusPubGhostPublisher';
  }

  /**
   * Check if video is enabled. It could be disabled by user but could also be disabled
   * by other reasons as this flag just tells us if the user local video is currently working on not.
   * In some cases user still might expect it to be working but it is not (video track might be disabled)
   */
  if (!media.hasVideo) {
    return 'psStatusPubVideoDisabled';
  }

  /**
   * Check if the video is actually rendering.
   */
  const videoElement = (publisher as Publisher).videoElement();

  if (!videoElement || videoElement.readyState < 2) {
    return 'psStatusPubVideoNotRendering';
  }

  /**
   * Check if the video track is muted.
   */
  if (videoTrack.muted) {
    return 'psStatusPubVideoTrackMuted';
  }

  return undefined;
};
